import { Card, Fade, Flex } from '@chakra-ui/react';

import { ChipTabs } from '~/components/chip-tab';
import { Glyph } from '~/components/glyph';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { Markdown } from '~/components/markdown';
import { TemplateButton } from '~/components/template-button';
import { TemplateButton as TemplateButtonProps } from '~/features/page-template/models/template-button';

export type MentorRoleTabsProps = {
  roles: {
    title: string;
    description: string;
    glyphName?: string;
    cta: TemplateButtonProps;
  }[];
};

export function MentorRoleTabs({ roles }: MentorRoleTabsProps) {
  return (
    <ChipTabs
      spacerHeight='24px'
      tabLabels={roles?.map((role) => role.title)}
      vertical={true}
      tabLabelProps={{
        width: '260px',
        padding: '15px 45px',
        borderRadius: '4px',
        _selected: {
          backgroundColor: 'blue.500',
          color: 'white',
          borderColor: 'blue.500',
        },
      }}
      tabListProps={{
        gap: '10px',
        overflowX: {
          base: 'auto',
          lg: 'hidden',
        },
      }}
      tabPanels={roles?.map((panel, idx) => (
        <Fade
          key={idx}
          animate={{ opacity: 1, transition: { ease: 'easeInOut' } }}
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
        >
          <TabPanel {...panel} />
        </Fade>
      ))}
    />
  );
}

export function TabPanel({ title, description, glyphName, cta }: MentorRoleTabsProps['roles'][number]) {
  return (
    <Card
      alignItems='center'
      backgroundColor='accent.sky.100'
      borderRadius='base'
      display='flex'
      gap={{ lg: '40px' }}
      padding={{ base: '64px 16px', lg: '40px' }}
      textAlign='left'
      variant='outline'
    >
      <Flex alignItems='center' flexDirection={{ base: 'column', lg: 'row' }} gap={{ base: '40px', lg: '80px' }}>
        <Flex flexDirection='column'>
          {glyphName && <Glyph name={glyphName} />}
          <HeadingWithEmphasis
            text={title}
            emphasisProps={{
              color: 'blue.500',
              fontWeight: 500,
            }}
            headingProps={{
              as: 'h2',
              mb: '8px',
              size: 'h1',
              margin: '6px 0',
            }}
          />
          <Markdown markdownText={description} />
          {cta && <TemplateButton marginTop='48px' {...cta} />}
        </Flex>
      </Flex>
    </Card>
  );
}
